import { BaseClient } from '../base-client';
import type {
  ApproveAllFilter,
  ApprovedSearchQuery,
  ApprovedSearchResult,
  BudgetSignalsQuery,
  BudgetSignalsResult,
  DecideBulkResult,
  PendingApprovalsQuery,
  PendingApprovalsResult,
  PendingCount,
} from './types';

type PendingResponse = { data: PendingApprovalsResult };
type CountResponse = { data: PendingCount };
type BulkDecideResponse = { data: DecideBulkResult };
type BudgetSignalsResponse = { data: BudgetSignalsResult };

/**
 * Client for the approvals page. The "pending for me this week" query +
 * sidebar count both live here. Bulk decide is on the generic approvals
 * client (`ApprovalInstancesClient`) because the endpoint itself is
 * entity-agnostic; we re-export a thin wrapper here for convenience.
 */
export class TimesheetApprovalsClient extends BaseClient {
  /**
   * Pending timesheet approvals awaiting the caller's decision. Week mode
   * passes a Monday/Sunday range; date-range mode passes an arbitrary window
   * (or omits both bounds for "all time") plus `groupBy`/`groupLimit` for the
   * group-atomic cap.
   */
  pending(query: PendingApprovalsQuery): Promise<PendingResponse> {
    const params: Record<string, string> = {};
    if (query.weekStartDate) params.weekStartDate = query.weekStartDate;
    if (query.weekEndDate) params.weekEndDate = query.weekEndDate;
    if (query.customerIds && query.customerIds.length > 0) {
      params.customerIds = query.customerIds.join(',');
    }
    if (query.userIds && query.userIds.length > 0) {
      params.userIds = query.userIds.join(',');
    }
    if (query.groupBy) params.groupBy = query.groupBy;
    if (query.groupLimit != null) params.groupLimit = String(query.groupLimit);
    return this.request('/api/timesheet-approvals/pending', { query: params });
  }

  /**
   * Approve every pending entry in the caller's queue matching the filter.
   * Used by the date-range view's "Approve all", which must cover the whole
   * matching set — not just the loaded groups. Returns the same per-instance
   * results array as `approveBulk` / `rejectBulk`.
   */
  approveAll(filter: ApproveAllFilter): Promise<BulkDecideResponse> {
    return this.request('/api/timesheet-approvals/approve-all', {
      method: 'POST',
      body: filter,
    });
  }

  /** Pending counts for the sidebar badge + page header summary. */
  count(): Promise<CountResponse> {
    return this.request('/api/timesheet-approvals/count');
  }

  /**
   * The timesheet over-budget signal for the caller's currently visible
   * pending approvals (same filter shape as `pending`, minus the group
   * cap). Requires `customer.budget.view` server-side — a caller without it
   * gets a 403, so the approvals page only calls this when it knows the
   * caller holds that permission.
   */
  budgetSignals(query: BudgetSignalsQuery): Promise<BudgetSignalsResponse> {
    const params: Record<string, string> = {};
    if (query.weekStartDate) params.weekStartDate = query.weekStartDate;
    if (query.weekEndDate) params.weekEndDate = query.weekEndDate;
    if (query.customerIds && query.customerIds.length > 0) {
      params.customerIds = query.customerIds.join(',');
    }
    if (query.userIds && query.userIds.length > 0) {
      params.userIds = query.userIds.join(',');
    }
    return this.request('/api/timesheet-approvals/budget-signals', { query: params });
  }

  /**
   * Search entries the caller ALREADY approved, for post-approval reject.
   * Server-scoped to the caller's own approvals; all filters optional.
   */
  searchApproved(query: ApprovedSearchQuery = {}): Promise<ApprovedSearchResult> {
    const params: Record<string, string> = {};
    if (query.fromDate) params.fromDate = query.fromDate;
    if (query.toDate) params.toDate = query.toDate;
    if (query.customerIds && query.customerIds.length > 0) {
      params.customerIds = query.customerIds.join(',');
    }
    if (query.userIds && query.userIds.length > 0) {
      params.userIds = query.userIds.join(',');
    }
    if (query.limit != null) params.limit = String(query.limit);
    if (query.offset != null) params.offset = String(query.offset);
    return this.request('/api/timesheet-approvals/approved', { query: params });
  }

  /**
   * Approve many instances in one request. Per-instance failures are
   * surfaced in `results` rather than aborting the whole batch.
   * Lives here so the approvals page only depends on one client.
   */
  approveBulk(instanceIds: string[]): Promise<BulkDecideResponse> {
    return this.request('/api/approval-instances/approve-bulk', {
      method: 'POST',
      body: { instanceIds },
    });
  }

  /**
   * Reject many instances with ONE shared reason (every UI bulk-reject
   * collects a single reason for its whole scope). Same per-instance
   * results contract as `approveBulk`.
   */
  rejectBulk(instanceIds: string[], reason: string): Promise<BulkDecideResponse> {
    return this.request('/api/approval-instances/reject-bulk', {
      method: 'POST',
      body: { instanceIds, reason },
    });
  }
}
